/**
 * Camera and projection.
 *
 * Projection is done in JS rather than via ctx.setTransform, deliberately.
 * Line widths, font sizes, arrowhead sizes and hit-test slop must all be
 * constant in SCREEN pixels (labels staying 11px legible at any zoom is the
 * Obsidian look). With a camera transform on the context you would have to
 * divide every one of those by z, which is both error-prone and wrong for
 * text. Projecting manually is one multiply-add per node, is needed for
 * culling anyway, and makes every LOD decision a plain screen comparison.
 */
export interface Camera {
  /** World point at the viewport centre. */
  x: number;
  y: number;
  /** Device-independent pixels per world unit. */
  z: number;
}

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 8;
export const FIT_PADDING = 60;

export function makeCamera(): Camera {
  return { x: 0, y: 0, z: 1 };
}

export function toScreenX(wx: number, cam: Camera, width: number): number {
  return (wx - cam.x) * cam.z + width / 2;
}

export function toScreenY(wy: number, cam: Camera, height: number): number {
  return (wy - cam.y) * cam.z + height / 2;
}

export function toWorld(sx: number, sy: number, cam: Camera, width: number, height: number): { x: number; y: number } {
  return { x: (sx - width / 2) / cam.z + cam.x, y: (sy - height / 2) / cam.z + cam.y };
}

export function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/**
 * Zoom about a screen point, keeping the world point under it fixed. That
 * invariant is the whole trick: solve for the camera that maps the same world
 * point back onto the same screen point at the new scale.
 */
export function zoomAt(cam: Camera, sx: number, sy: number, factor: number, width: number, height: number): Camera {
  const before = toWorld(sx, sy, cam, width, height);
  const z = clampZoom(cam.z * factor);
  return { z, x: before.x - (sx - width / 2) / z, y: before.y - (sy - height / 2) / z };
}

/**
 * Normalize wheel deltas. Firefox commonly reports deltaMode 1 (lines) with
 * deltaY of about 3, where Chromium reports pixels, so treating the raw value
 * as pixels makes Firefox zoom roughly 16x too slowly.
 */
export function wheelDelta(e: WheelEvent, viewportHeight: number): number {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * viewportHeight;
  return e.deltaY;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function boundsOf(pos: Float32Array, count: number): Bounds | null {
  if (count <= 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < count; i++) {
    const x = pos[i * 2]!;
    const y = pos[i * 2 + 1]!;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * Smallest world extent we will frame. Without a floor, a graph of one or two
 * nodes (the first moments of creation playback) zooms to the clamp and fills
 * the screen with a single circle.
 */
export const MIN_FIT_EXTENT = 900;

export function fitCamera(b: Bounds | null, width: number, height: number): Camera {
  if (!b || width <= 0 || height <= 0) return makeCamera();
  const w = Math.max(MIN_FIT_EXTENT, b.maxX - b.minX);
  const h = Math.max(MIN_FIT_EXTENT, b.maxY - b.minY);
  const z = clampZoom(Math.min((width - FIT_PADDING * 2) / w, (height - FIT_PADDING * 2) / h));
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2, z };
}

/** Ease the camera toward a target so auto-fit glides instead of snapping. */
export function easeCamera(from: Camera, to: Camera, s: number): Camera {
  return {
    x: from.x + (to.x - from.x) * s,
    y: from.y + (to.y - from.y) * s,
    // zoom eases geometrically, or the pull-back reads as a lurch
    z: from.z * Math.pow(to.z / from.z, s),
  };
}
